// =====================================================================
// ADMIN · PEDIDOS
//
// Lista de ventas (orders) con flujo de:
//   - Cambio de status (draft → confirmed → paid → shipped → delivered)
//   - Facturación al cliente vía Facturapi (botón "Facturar")
//   - Vista del CFDI emitido (folio + link a PDF/XML)
//   - Visualización de OCs auto-generadas (dropshipping fiscal)
// =====================================================================

import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { theme } from '../../../styles/theme';
import { useCurrency } from '../../../lib/currency';
import { AdminContainer } from '../AdminShell';
import { InvoiceDraftModal } from './InvoiceDraftModal';

type OrderRow = {
  id: string;
  order_number: string | null;
  customer_id: string | null;
  status: string;
  payment_status: string | null;
  total: number | null;
  currency: string;
  paid_currency: string | null;
  tipo_cambio: number | null;
  created_at: string;
  customer_name?: string;
  invoice_count?: number;
  po_count?: number;
  item_count?: number;
};

type Tab = 'all' | 'pending' | 'paid' | 'shipped' | 'delivered' | 'cancelled';

const STATUSES: Array<{ value: string; label: string }> = [
  { value: 'draft',     label: 'Borrador' },
  { value: 'confirmed', label: 'Confirmado' },
  { value: 'paid',      label: 'Pagado' },
  { value: 'shipped',   label: 'Enviado' },
  { value: 'delivered', label: 'Entregado' },
  { value: 'cancelled', label: 'Cancelado' },
];

export function AdminPedidos() {
  const [tab, setTab] = useState<Tab>('all');
  const [orders, setOrders] = useState<OrderRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const { data } = await supabase
        .from('orders')
        .select(`
          id, order_number, customer_id, status, payment_status, total, currency, paid_currency, tipo_cambio, created_at,
          customers ( business_name )
        `)
        .order('created_at', { ascending: false })
        .limit(500);

      const ids = ((data as any[]) ?? []).map(r => r.id);
      // Contar facturas + OCs + items en paralelo
      const [invs, pos, its] = await Promise.all([
        supabase.from('customer_invoices').select('order_id').in('order_id', ids),
        supabase.from('purchase_orders').select('triggered_by_order_id').in('triggered_by_order_id', ids),
        supabase.from('order_items').select('order_id').in('order_id', ids),
      ]);
      const invCount: Record<string, number> = {};
      for (const r of (invs.data as any[]) ?? []) {
        if (r.order_id) invCount[r.order_id] = (invCount[r.order_id] || 0) + 1;
      }
      const poCount: Record<string, number> = {};
      for (const r of (pos.data as any[]) ?? []) {
        if (r.triggered_by_order_id) poCount[r.triggered_by_order_id] = (poCount[r.triggered_by_order_id] || 0) + 1;
      }
      const itemCount: Record<string, number> = {};
      for (const r of (its.data as any[]) ?? []) {
        if (r.order_id) itemCount[r.order_id] = (itemCount[r.order_id] || 0) + 1;
      }

      const rows = ((data as any[]) ?? []).map(r => ({
        ...r,
        customer_name: r.customers?.business_name ?? null,
        invoice_count: invCount[r.id] || 0,
        po_count: poCount[r.id] || 0,
        item_count: itemCount[r.id] || 0,
      })) as OrderRow[];

      setOrders(rows);
      setLoading(false);
    })();
  }, [reloadKey]);

  const counts = useMemo(() => ({
    all: orders.length,
    pending: orders.filter(o => ['draft', 'confirmed'].includes(o.status)).length,
    paid: orders.filter(o => o.status === 'paid').length,
    shipped: orders.filter(o => o.status === 'shipped').length,
    delivered: orders.filter(o => o.status === 'delivered').length,
    cancelled: orders.filter(o => o.status === 'cancelled').length,
  }), [orders]);

  const filtered = useMemo(() => {
    return orders.filter(o => {
      if (tab === 'all') return true;
      if (tab === 'pending') return ['draft', 'confirmed'].includes(o.status);
      return o.status === tab;
    });
  }, [orders, tab]);

  return (
    <AdminContainer title="Pedidos" eyebrow={`${orders.length} totales`}>
      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 16, borderBottom: `1px solid ${theme.border}` }}>
        <TabBtn active={tab === 'all'} onClick={() => setTab('all')}>Todas <Mini>{counts.all}</Mini></TabBtn>
        <TabBtn active={tab === 'pending'} onClick={() => setTab('pending')}>Por pagar <Mini>{counts.pending}</Mini></TabBtn>
        <TabBtn active={tab === 'paid'} onClick={() => setTab('paid')}>Pagadas <Mini>{counts.paid}</Mini></TabBtn>
        <TabBtn active={tab === 'shipped'} onClick={() => setTab('shipped')}>Enviadas <Mini>{counts.shipped}</Mini></TabBtn>
        <TabBtn active={tab === 'delivered'} onClick={() => setTab('delivered')}>Entregadas <Mini>{counts.delivered}</Mini></TabBtn>
        <TabBtn active={tab === 'cancelled'} onClick={() => setTab('cancelled')}>Canceladas <Mini>{counts.cancelled}</Mini></TabBtn>
      </div>

      <div style={{ marginBottom: 8, fontSize: 12, color: theme.textMuted }}>
        {loading ? 'Cargando…' : `${filtered.length} de ${orders.length}`}
      </div>

      <div style={{ background: theme.bg, border: `1px solid ${theme.border}`, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 1100 }}>
          <thead>
            <tr style={{ background: theme.bgSoft }}>
              <Th>#</Th>
              <Th>Cliente</Th>
              <Th>Status</Th>
              <Th>Pago</Th>
              <Th>Pagado en</Th>
              <Th style={{ textAlign: 'right' }}>Total c/IVA</Th>
              <Th style={{ textAlign: 'center' }}>OCs</Th>
              <Th style={{ textAlign: 'center' }}>CFDI</Th>
              <Th>Creada</Th>
              <Th style={{ width: 110 }}> </Th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(o => (
              <tr key={o.id} style={{ borderBottom: `1px solid ${theme.border}`, cursor: 'pointer' }}
                  onClick={() => setDetailId(o.id)}>
                <Td>
                  <span style={{ fontFamily: theme.font.mono, fontSize: 11, color: theme.textMuted }}>
                    {o.order_number ?? o.id.slice(0, 8)}
                  </span>
                </Td>
                <Td><div style={{ fontWeight: 600, color: theme.brand }}>{o.customer_name ?? '—'}</div></Td>
                <Td><StatusTag status={o.status} /></Td>
                <Td><PaymentTag status={o.payment_status} /></Td>
                <Td>
                  {o.paid_currency
                    ? <>
                        <span style={{ fontWeight: 600 }}>{o.paid_currency}</span>
                        {o.paid_currency === 'MXN' && o.tipo_cambio != null && (
                          <div style={{ fontSize: 10, color: theme.textMuted, marginTop: 2 }}>@ {o.tipo_cambio} MXN/USD</div>
                        )}
                      </>
                    : <span style={{ color: theme.textMuted, fontSize: 11 }}>—</span>}
                </Td>
                <Td style={{ textAlign: 'right', fontWeight: 700 }}>
                  {o.total != null
                    ? (o.paid_currency === 'MXN' && o.tipo_cambio != null
                        ? `$${(Number(o.total) * 1.16 * Number(o.tipo_cambio)).toLocaleString('es-MX', { maximumFractionDigits: 2 })} MXN`
                        : `$${(Number(o.total) * 1.16).toLocaleString('en-US', { maximumFractionDigits: 2 })} ${o.currency}`)
                    : '—'}
                </Td>
                <Td style={{ textAlign: 'center' }}>
                  {o.po_count ? (
                    <span style={{ fontSize: 11, padding: '2px 8px', background: theme.accentSoft, color: theme.brand, fontWeight: 700 }}>
                      {o.po_count}
                    </span>
                  ) : <span style={{ color: theme.textMuted, fontSize: 11 }}>—</span>}
                </Td>
                <Td style={{ textAlign: 'center' }}>
                  {o.invoice_count ? (
                    <span style={{ fontSize: 11, padding: '2px 8px', background: '#DCFCE7', color: '#15803D', fontWeight: 700 }}>
                      ✓ {o.invoice_count}
                    </span>
                  ) : <span style={{ color: theme.textMuted, fontSize: 11 }}>—</span>}
                </Td>
                <Td style={{ fontSize: 11, color: theme.textMuted }}>
                  {new Date(o.created_at).toLocaleDateString('es-MX')}
                </Td>
                <Td onClick={e => e.stopPropagation()}>
                  {canInvoice(o) && o.invoice_count === 0 && (o.item_count ?? 0) > 0 && (
                    <button onClick={() => setDetailId(o.id)} style={btnInvoice}>
                      🧾 Facturar
                    </button>
                  )}
                  {(o.item_count ?? 0) === 0 && (
                    <span title="Orden histórica sin items" style={{ fontSize: 11, color: theme.textMuted }}>
                      Histórico
                    </span>
                  )}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>

        {filtered.length === 0 && !loading && (
          <div style={{ padding: 40, textAlign: 'center', color: theme.textMuted, fontSize: 13 }}>
            No hay pedidos en este filtro.
          </div>
        )}
      </div>

      {detailId && (
        <OrderDetail
          orderId={detailId}
          onClose={() => setDetailId(null)}
          onChanged={() => setReloadKey(k => k + 1)}
        />
      )}
    </AdminContainer>
  );
}

function canInvoice(o: OrderRow): boolean {
  return ['confirmed', 'paid', 'shipped', 'delivered'].includes(o.status);
}

// =====================================================================
// ORDER DETAIL — modal con items + acciones + facturación
// =====================================================================

type OrderFull = {
  id: string;
  order_number: string | null;
  customer_id: string | null;
  status: string;
  payment_status: string | null;
  subtotal: number | null;
  total: number | null;
  tax_amount: number | null;
  shipping_amount: number | null;
  currency: string;
  tipo_cambio: number | null;
  paid_currency: string | null;
  estimated_delivery: string | null;
  eta_manual: boolean | null;
  notes: string | null;
  shipping_address: any;
  created_at: string;
  customers?: {
    business_name: string;
    rfc: string | null;
    regimen_fiscal: string | null;
    cfdi_use: string | null;
    primary_email: string | null;
  } | null;
};

type OrderItem = {
  id: string;
  sku: string;
  brand_code: string;
  description: string;
  quantity: number;
  unit: string | null;
  unit_price: number;
  discount_pct: number;
  line_total: number;
};

type Invoice = {
  id: string;
  facturapi_id: string;
  invoice_number: string | null;
  uuid_fiscal: string | null;
  fecha_emision: string | null;
  total: number;
  xml_url: string | null;
  pdf_url: string | null;
  estado: string;
};

type PO = {
  id: string;
  po_number: string;
  po_type: string | null;
  status: string;
  total: number | null;
  invoice_received_at: string | null;
};

function OrderDetail({ orderId, onClose, onChanged }: {
  orderId: string; onClose: () => void; onChanged: () => void;
}) {
  const { usdMxn } = useCurrency();
  const [order, setOrder] = useState<OrderFull | null>(null);
  const [items, setItems] = useState<OrderItem[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [pos, setPos] = useState<PO[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [draftId, setDraftId] = useState<string | null>(null);
  // El pedido se guarda en USD; si el pago fue en MXN se MUESTRA en pesos al TC fijo.
  const paidMxn = order?.paid_currency === 'MXN' && order?.tipo_cambio != null;
  const payTc = paidMxn ? Number(order!.tipo_cambio) : 1;
  const payCur = paidMxn ? 'MXN' : (order?.currency ?? 'USD');
  const fmtPay = (usd: number | null | undefined) =>
    `$${((usd ?? 0) * payTc).toLocaleString(paidMxn ? 'es-MX' : 'en-US', { maximumFractionDigits: 2 })} ${payCur}`;

  const load = async () => {
    setLoading(true);
    const [oRes, iRes, invRes, poRes] = await Promise.all([
      supabase.from('orders').select(`
        *, customers ( business_name, rfc, regimen_fiscal, cfdi_use, primary_email )
      `).eq('id', orderId).maybeSingle(),
      supabase.from('order_items').select('*').eq('order_id', orderId).order('id'),
      supabase.from('customer_invoices').select('id, facturapi_id, invoice_number, uuid_fiscal, fecha_emision, total, xml_url, pdf_url, estado').eq('order_id', orderId),
      supabase.from('purchase_orders').select('id, po_number, po_type, status, total, invoice_received_at').eq('triggered_by_order_id', orderId),
    ]);
    setOrder(oRes.data as OrderFull);
    {
      const o = oRes.data as any;
      if (o && !o.eta_manual && !o.estimated_delivery) {
        supabase.rpc('plan_order_fulfillment', { p_order_id: orderId }).then(({ data: plan }) => {
          const d = (plan as any)?.summary?.ready_estimate_date;
          if (d) {
            supabase.from('orders').update({ estimated_delivery: d }).eq('id', orderId).then(() => {});
            setOrder(prev => prev ? { ...prev, estimated_delivery: d } : prev);
          }
        });
      }
    }
    setItems((iRes.data as OrderItem[]) ?? []);
    setInvoices((invRes.data as Invoice[]) ?? []);
    setPos((poRes.data as PO[]) ?? []);
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [orderId]);

  async function changeStatus(newStatus: string) {
    if (!order) return;
    setBusy(true);
    setErr(null);
    const { error } = await supabase.from('orders').update({ status: newStatus }).eq('id', orderId);
    setBusy(false);
    if (error) { setErr(error.message); return; }
    await load();
    onChanged();
  }

  async function saveEta(date: string) {
    if (!order) return;
    const val = date || null;
    const { error } = await supabase.from('orders').update({ estimated_delivery: val, eta_manual: true }).eq('id', orderId);
    if (error) { setErr(error.message); return; }
    setOrder(prev => prev ? { ...prev, estimated_delivery: val, eta_manual: true } : prev);
    onChanged();
  }
  async function recalcEta() {
    if (!order) return;
    const { data: plan } = await supabase.rpc('plan_order_fulfillment', { p_order_id: orderId });
    const d = (plan as any)?.summary?.ready_estimate_date ?? null;
    const { error } = await supabase.from('orders').update({ estimated_delivery: d, eta_manual: false }).eq('id', orderId);
    if (error) { setErr(error.message); return; }
    setOrder(prev => prev ? { ...prev, estimated_delivery: d } : prev);
    onChanged();
  }

  // Crea (o reabre) un borrador editable de CFDI y lo abre en el modal de facturación.
  // El usuario podrá editar receptor / conceptos / pago / moneda / notas, previsualizar y timbrar.
  async function openDraft() {
    if (!order) return;
    setBusy(true);
    setErr(null);
    const { data, error } = await supabase.rpc('prepare_invoice_draft', { p_order_id: orderId });
    setBusy(false);
    if (error) {
      setErr(error.message);
      return;
    }
    if (!data) {
      setErr('No se pudo preparar el borrador');
      return;
    }
    setDraftId(data as string);
  }

  const customer = order?.customers;
  const canEmit = order && canInvoice(order as any) && invoices.length === 0 && items.length > 0;
  const missingFiscalData = customer && (!customer.rfc || !customer.regimen_fiscal || !customer.cfdi_use);

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: 16,
    }}>
      <div style={{
        background: theme.bg, border: `1px solid ${theme.border}`,
        width: '100%', maxWidth: 1100,
        maxHeight: '94vh', display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 28px', borderBottom: `1px solid ${theme.border}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div>
            <div style={{ fontSize: 11, color: theme.accentDark, letterSpacing: 1.5, textTransform: 'uppercase', fontWeight: 700 }}>
              Pedido {order?.order_number ?? orderId.slice(0, 8)}
            </div>
            <h2 style={{ fontFamily: theme.font.head, fontSize: 22, fontWeight: 400, color: theme.brand, margin: '4px 0 0' }}>
              {customer?.business_name ?? '—'}
            </h2>
          </div>
          <button onClick={onClose} style={closeBtn}>×</button>
        </div>

        {/* Body */}
        <div style={{ padding: 28, overflowY: 'auto', flex: 1 }}>
          {loading || !order ? (
            <div style={{ padding: 40, textAlign: 'center', color: theme.textMuted }}>Cargando…</div>
          ) : (
            <>
              {/* Meta + status */}
              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16,
                marginBottom: 20, padding: 16, background: theme.bgSoft, border: `1px solid ${theme.border}`,
              }}>
                <Meta label="Status" value={
                  <select
                    value={order.status} disabled={busy}
                    onChange={e => changeStatus(e.target.value)}
                    style={{ ...inputStyle, padding: '6px 10px', fontSize: 12, cursor: busy ? 'wait' : 'pointer', width: 'auto' }}
                  >
                    {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                } />
                <Meta label="Pago" value={order.payment_status ?? '—'} />
                <Meta label="Pagado en" value={order.paid_currency ?? '—'} />
                {order.paid_currency === 'MXN' && (
                  <Meta label="Tipo de cambio (pago)" value={order.tipo_cambio != null ? `$${order.tipo_cambio} MXN/USD` : '—'} />
                )}
                <Meta label="Total (con IVA)" value={fmtPay((order.total ?? 0) * 1.16)} />
                <Meta label="Creada" value={new Date(order.created_at).toLocaleDateString('es-MX')} />
                <Meta label="Entrega estimada" value={
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                    <input type="date" value={order.estimated_delivery ?? ''} onChange={e => saveEta(e.target.value)}
                      style={{ ...inputStyle, padding: '6px 10px', fontSize: 12, width: 'auto' }} />
                    {order.eta_manual
                      ? <button type="button" onClick={recalcEta} title="Volver a cálculo automático" style={{ background: 'transparent', border: 'none', color: theme.accentDark, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>↻ auto</button>
                      : <span style={{ fontSize: 10, color: theme.textMuted }}>auto</span>}
                  </div>
                } />
                {customer?.rfc && <Meta label="RFC cliente" value={customer.rfc} />}
                {customer?.primary_email && <Meta label="Email cliente" value={customer.primary_email} />}
              </div>

              {/* Items */}
              <h3 style={sectionH3}>Productos</h3>
              <div style={{ background: theme.bg, border: `1px solid ${theme.border}`, overflowX: 'auto', marginBottom: 20 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 700 }}>
                  <thead>
                    <tr style={{ background: theme.bgSoft }}>
                      <Th>SKU / Producto</Th>
                      <Th style={{ textAlign: 'right', width: 80 }}>Qty</Th>
                      <Th style={{ textAlign: 'right', width: 110 }}>Unit</Th>
                      <Th style={{ textAlign: 'right', width: 80 }}>Desc</Th>
                      <Th style={{ textAlign: 'right', width: 130 }}>Total</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map(it => (
                      <tr key={it.id} style={{ borderBottom: `1px solid ${theme.border}` }}>
                        <Td>
                          <div style={{ fontFamily: theme.font.mono, fontSize: 11, color: theme.textMuted }}>
                            {it.sku} · {it.brand_code}
                          </div>
                          <div style={{ fontSize: 13, color: theme.brand, marginTop: 2 }}>{it.description}</div>
                        </Td>
                        <Td style={{ textAlign: 'right' }}>{it.quantity}</Td>
                        <Td style={{ textAlign: 'right' }}>{fmtPay(it.unit_price)}</Td>
                        <Td style={{ textAlign: 'right' }}>{it.discount_pct}%</Td>
                        <Td style={{ textAlign: 'right', fontWeight: 700 }}>
                          {fmtPay(it.line_total)}
                          {!paidMxn && usdMxn && (
                            <div style={{ fontSize: 10, color: theme.textMuted, fontWeight: 500 }}>
                              ≈ ${(it.line_total * usdMxn).toLocaleString('es-MX', { maximumFractionDigits: 0 })} MXN
                            </div>
                          )}
                        </Td>
                      </tr>
                    ))}
                    {items.length === 0 && (
                      <tr><td colSpan={5} style={{ padding: 0 }}>
                        <div style={{
                          margin: 16, padding: 14,
                          background: 'rgba(245,158,11,0.08)', border: `1px solid #F59E0B`,
                          fontSize: 12, color: '#92400E', lineHeight: 1.5,
                        }}>
                          <div style={{ fontWeight: 700, marginBottom: 4 }}>
                            ⚠️ Orden histórica migrada desde BigCommerce
                          </div>
                          Esta orden conserva solo el <strong>header</strong> (cliente, total, status, fechas)
                          como referencia comercial. Los items individuales no se migraron — probablemente
                          ya fue facturada en su momento desde BC. No se puede emitir CFDI nuevo de esta orden.
                          <div style={{ marginTop: 8, fontSize: 11 }}>
                            Las ventas nuevas (creadas desde el cotizador NULED-OS) sí incluyen items completos.
                          </div>
                        </div>
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Plan de surtido multi-bodega */}
              <FulfillmentPlan orderId={orderId} />

              {/* OCs auto-generadas */}
              {pos.length > 0 && (
                <>
                  <h3 style={sectionH3}>Órdenes de compra generadas (dropshipping fiscal)</h3>
                  <div style={{ background: theme.bg, border: `1px solid ${theme.border}`, marginBottom: 20 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                      <thead>
                        <tr style={{ background: theme.bgSoft }}>
                          <Th>OC</Th><Th>Tipo</Th><Th>Status</Th>
                          <Th style={{ textAlign: 'right' }}>Total</Th><Th>Factura</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {pos.map(p => (
                          <tr key={p.id} style={{ borderBottom: `1px solid ${theme.border}` }}>
                            <Td><span style={{ fontFamily: theme.font.mono, fontSize: 11 }}>{p.po_number}</span></Td>
                            <Td>{p.po_type}</Td>
                            <Td>{p.status}</Td>
                            <Td style={{ textAlign: 'right' }}>${Number(p.total).toLocaleString('en-US', { maximumFractionDigits: 2 })}</Td>
                            <Td>
                              {p.invoice_received_at
                                ? <span style={{ color: '#15803D', fontWeight: 700 }}>✓ {new Date(p.invoice_received_at).toLocaleDateString('es-MX')}</span>
                                : <span style={{ color: theme.textMuted }}>Pendiente</span>}
                            </Td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {/* CFDIs emitidos */}
              {invoices.length > 0 && (
                <>
                  <h3 style={sectionH3}>CFDIs emitidos al cliente</h3>
                  <div style={{ background: theme.bg, border: `1px solid ${theme.border}`, marginBottom: 20 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                      <thead>
                        <tr style={{ background: theme.bgSoft }}>
                          <Th>Folio</Th><Th>UUID Fiscal</Th><Th>Fecha</Th>
                          <Th style={{ textAlign: 'right' }}>Total</Th><Th>Estado</Th><Th>Archivos</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoices.map(inv => (
                          <tr key={inv.id} style={{ borderBottom: `1px solid ${theme.border}` }}>
                            <Td><span style={{ fontFamily: theme.font.mono, fontSize: 11 }}>{inv.invoice_number}</span></Td>
                            <Td><span style={{ fontFamily: theme.font.mono, fontSize: 10, color: theme.textMuted }}>{inv.uuid_fiscal?.slice(0, 8)}…</span></Td>
                            <Td>{inv.fecha_emision ? new Date(inv.fecha_emision).toLocaleDateString('es-MX') : '—'}</Td>
                            <Td style={{ textAlign: 'right', fontWeight: 700 }}>${Number(inv.total).toLocaleString('en-US', { maximumFractionDigits: 2 })}</Td>
                            <Td>
                              <span style={{
                                fontSize: 10, padding: '2px 6px', fontWeight: 700,
                                background: inv.estado === 'active' ? '#DCFCE7' : '#FEE2E2',
                                color: inv.estado === 'active' ? '#15803D' : '#B91C1C',
                              }}>
                                {inv.estado}
                              </span>
                            </Td>
                            <Td>
                              {inv.pdf_url && <a href={inv.pdf_url} target="_blank" rel="noreferrer" style={fileLink}>📄 PDF</a>}
                              {inv.xml_url && <a href={inv.xml_url} target="_blank" rel="noreferrer" style={{ ...fileLink, marginLeft: 6 }}>📋 XML</a>}
                            </Td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {/* Datos faltantes para facturar (solo si tiene items) */}
              {missingFiscalData && items.length > 0 && canInvoice(order as any) && invoices.length === 0 && (
                <div style={{
                  padding: 14, marginBottom: 16,
                  background: 'rgba(245,158,11,0.08)', border: `1px solid #F59E0B`,
                  fontSize: 12, color: '#92400E', lineHeight: 1.5,
                }}>
                  ⚠️ Para facturar a este cliente faltan datos fiscales:
                  {!customer?.rfc && <div>· RFC</div>}
                  {!customer?.regimen_fiscal && <div>· Régimen fiscal</div>}
                  {!customer?.cfdi_use && <div>· Uso CFDI</div>}
                  <div style={{ marginTop: 6 }}>
                    Cárgalos en <strong>/admin/clientes → editar cliente</strong> (puedes subir el CSF y se auto-rellena).
                  </div>
                </div>
              )}

              {err && (
                <div style={{
                  padding: 14, marginBottom: 16,
                  background: 'rgba(220,38,38,0.05)', border: `1px solid ${theme.err}`,
                  color: theme.err, fontSize: 13,
                }}>
                  {err}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {!loading && order && (
          <div style={{
            padding: '14px 28px', borderTop: `1px solid ${theme.border}`,
            display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12, flexWrap: 'wrap',
          }}>
            {canEmit && (
              <button
                onClick={openDraft}
                disabled={busy || !!missingFiscalData}
                title={missingFiscalData ? 'Captura RFC + régimen + uso CFDI primero' : 'Preparar borrador editable de CFDI antes de timbrar'}
                style={{
                  background: missingFiscalData ? theme.bgSoft : theme.accent,
                  color: missingFiscalData ? theme.textMuted : theme.brand,
                  border: 'none', padding: '12px 22px', fontSize: 13, fontWeight: 700,
                  textTransform: 'uppercase', letterSpacing: 0.5,
                  cursor: busy || missingFiscalData ? 'not-allowed' : 'pointer',
                  fontFamily: theme.font.sans,
                  opacity: busy ? 0.6 : 1,
                }}
              >
                {busy ? 'Preparando…' : '🧾 Preparar factura'}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Modal de borrador editable de CFDI */}
      {draftId && (
        <InvoiceDraftModal
          draftId={draftId}
          onClose={() => setDraftId(null)}
          onStamped={async () => {
            setDraftId(null);
            await load();
            onChanged();
          }}
        />
      )}
    </div>
  );
}

// =====================================================================
// PLAN DE SURTIDO — multi-bodega (Oficina / GRUPO ED / Proveedor)
// =====================================================================

type PlanLine = {
  item_id: string; sku: string; brand_code: string; description: string;
  needed: number; from_office: number; from_ed: number; from_supplier: number;
  office_avail: number; ed_avail: number; stock_policy: string; source: string; ready_date: string;
};
type Plan = {
  order_id: string; office_code: string; ed_code: string; next_ed_pickup_date: string;
  lines: PlanLine[];
  summary: {
    can_fulfill_from_office: boolean; needs_ed_pickup: boolean; needs_supplier: boolean;
    ready_estimate_date: string; line_count: number;
  };
};

function FulfillmentPlan({ orderId }: { orderId: string }) {
  const [plan, setPlan] = useState<Plan | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [reservedQty, setReservedQty] = useState(0);
  const [busy, setBusy] = useState(false);

  const loadPlan = async () => {
    setLoading(true); setErr(null);
    const { data, error } = await supabase.rpc('plan_order_fulfillment', { p_order_id: orderId });
    if (error) setErr(error.message); else setPlan(data as Plan);
    // asignaciones activas (reserva real)
    const { data: allocs } = await supabase
      .from('stock_allocations')
      .select('qty, status, order_item:order_items!inner(order_id)')
      .eq('order_item.order_id', orderId)
      .neq('status', 'released');
    setReservedQty(((allocs as any[]) ?? []).reduce((a, x) => a + Number(x.qty || 0), 0));
    setLoading(false);
  };

  useEffect(() => { loadPlan(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [orderId]);

  async function generarSurtido() {
    setBusy(true);
    // 1) reservar inventario disponible  2) generar OC (borrador) del faltante por proveedor
    const { error } = await supabase.rpc('apply_order_allocations', { p_order_id: orderId });
    if (!error) await supabase.rpc('generate_shortfall_pos', { p_order_id: orderId });
    setBusy(false);
    if (error) { alert(error.message); return; }
    await loadPlan();
  }
  async function liberarReserva() {
    if (!confirm('¿Liberar la reserva de inventario de este pedido? Vuelve a quedar disponible.')) return;
    setBusy(true);
    const { error } = await supabase.rpc('release_order_allocations', { p_order_id: orderId });
    setBusy(false);
    if (error) { alert(error.message); return; }
    await loadPlan();
  }

  if (loading) return (
    <><h3 style={sectionH3}>Plan de surtido</h3>
    <div style={{ padding: 16, color: theme.textMuted, fontSize: 13 }}>Calculando plan de surtido…</div></>
  );
  if (err) return (
    <><h3 style={sectionH3}>Plan de surtido</h3>
    <div style={{ padding: 12, marginBottom: 20, background: 'rgba(220,38,38,0.05)', border: `1px solid ${theme.err}`, color: theme.err, fontSize: 12 }}>{err}</div></>
  );
  if (!plan || plan.summary.line_count === 0) return null;

  const s = plan.summary;
  const fmtDate = (d: string) => new Date(d + 'T00:00:00').toLocaleDateString('es-MX', { weekday: 'short', day: 'numeric', month: 'short' });

  return (
    <>
      <h3 style={sectionH3}>Plan de surtido</h3>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
        {s.can_fulfill_from_office ? (
          <PlanBadge bg="#DCFCE7" fg="#15803D">✓ Se surte todo desde Oficina</PlanBadge>
        ) : (
          <>
            {s.needs_ed_pickup && (
              <PlanBadge bg="#FEF3C7" fg="#92400E">
                🚚 Recolección en GRUPO ED · próx. ruta {fmtDate(plan.next_ed_pickup_date)}
              </PlanBadge>
            )}
            {s.needs_supplier && (
              <PlanBadge bg="#FEE2E2" fg="#B91C1C">📦 Requiere pedido a proveedor</PlanBadge>
            )}
          </>
        )}
        <PlanBadge bg={theme.bgSoft} fg={theme.brand}>Listo estimado: {fmtDate(s.ready_estimate_date)}</PlanBadge>
        {reservedQty > 0 && <PlanBadge bg="#E0E7FF" fg="#3730A3">🔒 {reservedQty} pzas reservadas</PlanBadge>}
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <button onClick={generarSurtido} disabled={busy}
          style={{ background: theme.accent, color: '#0e0e0e', border: 'none', padding: '8px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: theme.font.sans }}>
          {busy ? 'Procesando…' : (reservedQty > 0 ? '↻ Actualizar surtido y compras' : '📦 Generar surtido y compras (reservar + OC faltante)')}
        </button>
        {reservedQty > 0 && (
          <button onClick={liberarReserva} disabled={busy}
            style={{ background: 'transparent', color: '#B91C1C', border: `1px solid ${theme.border}`, padding: '8px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: theme.font.sans }}>
            Liberar reserva
          </button>
        )}
      </div>
      <div style={{ background: theme.bg, border: `1px solid ${theme.border}`, overflowX: 'auto', marginBottom: 20 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 720 }}>
          <thead>
            <tr style={{ background: theme.bgSoft }}>
              <Th>SKU / Producto</Th>
              <Th style={{ textAlign: 'right', width: 70 }}>Pedido</Th>
              <Th style={{ textAlign: 'right', width: 80 }}>Oficina</Th>
              <Th style={{ textAlign: 'right', width: 90 }}>GRUPO ED</Th>
              <Th style={{ textAlign: 'right', width: 90 }}>Proveedor</Th>
              <Th style={{ width: 150 }}>Origen</Th>
            </tr>
          </thead>
          <tbody>
            {plan.lines.map(l => (
              <tr key={l.item_id} style={{ borderBottom: `1px solid ${theme.border}` }}>
                <Td>
                  <div style={{ fontFamily: theme.font.mono, fontSize: 11, color: theme.textMuted }}>{l.sku} · {l.brand_code}</div>
                  <div style={{ fontSize: 12, color: theme.brand, marginTop: 2 }}>{l.description}</div>
                </Td>
                <Td style={{ textAlign: 'right', fontWeight: 700 }}>{l.needed}</Td>
                <Td style={{ textAlign: 'right', color: l.from_office > 0 ? '#15803D' : theme.textMuted }}>
                  {l.from_office}<div style={{ fontSize: 9, color: theme.textMuted }}>de {l.office_avail}</div>
                </Td>
                <Td style={{ textAlign: 'right', color: l.from_ed > 0 ? '#92400E' : theme.textMuted }}>
                  {l.from_ed}<div style={{ fontSize: 9, color: theme.textMuted }}>de {l.ed_avail}</div>
                </Td>
                <Td style={{ textAlign: 'right', color: l.from_supplier > 0 ? '#B91C1C' : theme.textMuted }}>{l.from_supplier}</Td>
                <Td><SourceTag source={l.source} /></Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function PlanBadge({ children, bg, fg }: { children: React.ReactNode; bg: string; fg: string }) {
  return <span style={{ fontSize: 11, fontWeight: 700, padding: '6px 10px', background: bg, color: fg }}>{children}</span>;
}

function SourceTag({ source }: { source: string }) {
  const map: Record<string, { bg: string; fg: string; label: string }> = {
    office:    { bg: '#DCFCE7', fg: '#15803D', label: 'Oficina' },
    ed:        { bg: '#FEF3C7', fg: '#92400E', label: 'Recolección ED' },
    supplier:  { bg: '#FEE2E2', fg: '#B91C1C', label: 'Proveedor' },
    fulfilled: { bg: '#E0E7FF', fg: '#3730A3', label: 'Surtido' },
  };
  const c = map[source] ?? { bg: theme.bgSoft, fg: theme.brand, label: source };
  return (
    <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', background: c.bg, color: c.fg, textTransform: 'uppercase', letterSpacing: 0.4 }}>
      {c.label}
    </span>
  );
}

// =====================================================================
// Helpers
// =====================================================================

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick} style={{
      background: 'transparent', border: 'none', cursor: 'pointer',
      padding: '12px 18px', fontSize: 12, fontWeight: 700,
      color: active ? theme.brand : theme.textMuted,
      borderBottom: `2px solid ${active ? theme.accent : 'transparent'}`,
      textTransform: 'uppercase', letterSpacing: 0.5, fontFamily: theme.font.sans,
    }}>
      {children}
    </button>
  );
}

function Mini({ children }: { children: React.ReactNode }) {
  return <span style={{ marginLeft: 6, fontSize: 10, color: theme.textMuted, fontWeight: 500 }}>({children})</span>;
}

function Th({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <th style={{
      padding: '12px 14px', textAlign: 'left',
      fontSize: 10, fontWeight: 700, color: theme.textMuted,
      textTransform: 'uppercase', letterSpacing: 0.5,
      ...style,
    }}>{children}</th>
  );
}

function Td({ children, style, colSpan, onClick }: {
  children: React.ReactNode; style?: React.CSSProperties; colSpan?: number;
  onClick?: (e: React.MouseEvent<HTMLTableCellElement>) => void;
}) {
  return <td onClick={onClick} colSpan={colSpan} style={{ padding: '10px 14px', verticalAlign: 'middle', ...style }}>{children}</td>;
}

function Meta({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div style={{ fontSize: 10, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700, marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontSize: 13, color: theme.brand, fontWeight: 500 }}>{value}</div>
    </div>
  );
}

function StatusTag({ status }: { status: string }) {
  const colors: Record<string, { bg: string; fg: string }> = {
    draft:     { bg: '#F3F4F6', fg: '#6B7280' },
    confirmed: { bg: '#DBEAFE', fg: '#1E40AF' },
    paid:      { bg: '#DCFCE7', fg: '#15803D' },
    shipped:   { bg: '#FEF3C7', fg: '#92400E' },
    delivered: { bg: '#E0E7FF', fg: '#3730A3' },
    cancelled: { bg: '#FEE2E2', fg: '#B91C1C' },
  };
  const c = colors[status] || colors.draft;
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: '2px 8px',
      background: c.bg, color: c.fg,
      textTransform: 'uppercase', letterSpacing: 0.5,
    }}>{STATUSES.find(s => s.value === status)?.label ?? status}</span>
  );
}

function PaymentTag({ status }: { status: string | null }) {
  if (!status) return <span style={{ color: theme.textMuted, fontSize: 11 }}>—</span>;
  const colors: Record<string, { bg: string; fg: string }> = {
    pending:  { bg: '#FEF3C7', fg: '#92400E' },
    paid:     { bg: '#DCFCE7', fg: '#15803D' },
    refunded: { bg: '#FEE2E2', fg: '#B91C1C' },
    partial:  { bg: '#FED7AA', fg: '#9A3412' },
  };
  const c = colors[status] || { bg: '#F3F4F6', fg: '#6B7280' };
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: '2px 8px',
      background: c.bg, color: c.fg,
      textTransform: 'uppercase', letterSpacing: 0.5,
    }}>{status}</span>
  );
}

const sectionH3: React.CSSProperties = {
  fontFamily: theme.font.head, fontSize: 14, fontWeight: 700,
  color: theme.brand, margin: '0 0 10px',
  textTransform: 'uppercase', letterSpacing: 0.6,
};

const inputStyle: React.CSSProperties = {
  width: '100%', background: theme.bg, border: `1px solid ${theme.border}`,
  padding: '12px 14px', color: theme.text, fontSize: 14,
  fontFamily: theme.font.sans, outline: 'none', boxSizing: 'border-box',
};

const btnInvoice: React.CSSProperties = {
  background: '#15803D', color: '#FFFFFF', border: 'none',
  padding: '5px 10px', fontSize: 10, fontWeight: 700,
  textTransform: 'uppercase', letterSpacing: 0.4, cursor: 'pointer',
  fontFamily: theme.font.sans,
};

const closeBtn: React.CSSProperties = {
  background: 'transparent', border: 'none',
  fontSize: 28, color: theme.textMuted, cursor: 'pointer',
  padding: 0, lineHeight: 1, fontFamily: theme.font.sans,
};

const fileLink: React.CSSProperties = {
  fontSize: 11, color: theme.brand, textDecoration: 'none',
  padding: '3px 8px', border: `1px solid ${theme.border}`, background: theme.bg,
  display: 'inline-block',
};
